import { useDroppable } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { memo, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { DropdownMenu, type MenuSection } from '@features/shared/components/DropdownMenu'
import { IconButton } from '@features/shared/components/IconButton'
import { POPOVER_MENU_ICON_SIZE_PX } from '@features/shared/components/PopoverMenu'
import { focusQuietly } from '@features/shared/utils/quietFocus'
import { useTableOrganizationStore } from '../stores/useTableOrganizationStore'
import TableLink from './TableLink'
import { useTableListActions } from './tableListActions'
import { KEEP_ORDER_STRATEGY, useTableListRules } from './tableListRules'
import {
  ChevronDownIcon,
  EditIcon,
  FolderIcon,
  FolderOpenIcon,
  KebabIcon,
  TrashIcon,
} from '../../shared/icons/icons'
import {
  FOLDER_CHEVRON_ICON_GAP,
  FOLDER_CHEVRON_SIZE,
  FOLDER_ICON_LABEL_GAP,
  FOLDER_ICON_SIZE,
  getChildRowInset,
  getFolderGuideLeft,
  getFolderLabelLeft,
  getFolderRowInset,
  getFolderRowPaddingLeft,
} from '../utils/rowGeometry'
import type { TableSidebarItem } from '../api/contracts'
import type { FolderNode } from '../models'
import { DRAG_SWAPPED_ICON_CLASS, LeadingDragHandle } from './LeadingDragHandle'
import {
  MENU_ICON_SLOT_CLASS,
  MENU_ICON_SLOTS_CLASS,
  MENU_ROW_END_PAD_CLASS,
  menuIconSlotClass,
  menuIconSlotsLeadClass,
} from './menuIconSlots'
import FadeTruncatedText from '@features/shared/components/FadeTruncatedText'

interface FolderItemProps {
  folder: FolderNode
  /** All tables — used to look up table objects by id */
  tablesById: Map<string, TableSidebarItem>
  /** Nesting depth (0 = root). Used for indent. */
  depth: number
  siblingFolderCount: number
}

/**
 * A single folder row + (when expanded) its children: nested folders followed
 * by tables in this folder. Recursive — child folders render FolderItem too.
 */
function FolderItem({ folder, tablesById, depth, siblingFolderCount }: FolderItemProps) {
  const rules = useTableListRules()
  const isStoredExpanded = useTableOrganizationStore((s) => s.expandedFolderIds.includes(folder.id))
  const isExpanded = rules.searchExpansion
    ? !rules.searchExpansion.collapsedFolderIds.has(folder.id)
    : isStoredExpanded
  const toggle = useTableOrganizationStore((s) => s.toggleFolder)
  const { renameFolder, deleteFolder } = useTableListActions()
  const [menuOpen, setMenuOpen] = useState(false)
  // The name being typed while the folder is renamed in place (null otherwise).
  const [draftName, setDraftName] = useState<string | null>(null)
  const isRenaming = draftName !== null
  const rowRef = useRef<HTMLDivElement | null>(null)
  const nameInputRef = useRef<HTMLInputElement | null>(null)
  // The name starts selected, so typing replaces it. Done once the field is
  // in place: the menu that chose Rename folder hands focus back as it closes.
  // A rename ended from the keyboard hands focus back to the row once it's
  // a button (focusable) again.
  const refocusRowRef = useRef(false)
  useEffect(() => {
    if (isRenaming) {
      nameInputRef.current?.focus()
      nameInputRef.current?.select()
    } else if (refocusRowRef.current) {
      refocusRowRef.current = false
      if (rowRef.current) focusQuietly(rowRef.current, { preventScroll: true })
    }
  }, [isRenaming])

  // A folder moves among its siblings, into another folder, or (from inside
  // one) to the top level.
  const canMoveFolder =
    rules.reorderEnabled && (depth > 0 || siblingFolderCount > 1 || rules.folderCount > 1)
  const nestedRowInset = getFolderRowInset(depth)
  const folderRowPaddingLeft = getFolderRowPaddingLeft(depth)
  const childGuideLeft = getFolderGuideLeft(depth)
  const rowStrategy = rules.reorderEnabled ? verticalListSortingStrategy : KEEP_ORDER_STRATEGY
  const childFolderIds = folder.children.map((child) => `folder-${child.id}`)
  const tableIds = folder.tables.map((tableRef) => `table-${tableRef.table_id}`)

  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `folder-${folder.id}`,
    data: { type: 'folder', folderId: folder.id, parentId: folder.parent_id },
    disabled: !canMoveFolder,
  })

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop-${folder.id}`,
    data: { type: 'folder-drop', folderId: folder.id },
  })
  // The header under a dragged table or folder is where it will land.
  const isDropTarget = isOver

  // The folder's icon gives way to its drag handle on hover (LeadingDragHandle).
  const swappedIconClass = rules.reorderEnabled ? DRAG_SWAPPED_ICON_CLASS : ''

  const toggleFolder = () =>
    rules.searchExpansion ? rules.searchExpansion.toggleFolder(folder.id) : toggle(folder.id)
  const startRename = () => setDraftName(folder.name)
  // Saves a new, non-blank name; either way the row shows the name again.
  const finishRename = (save: boolean, refocusRow: boolean) => {
    const name = draftName?.trim()
    setDraftName(null)
    if (save && name && name !== folder.name) renameFolder?.(folder.id, name)
    // A blur as the field goes away never cancels a keyboard ending.
    if (refocusRow) refocusRowRef.current = true
  }
  const handleRowKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'F2' && renameFolder) {
      event.preventDefault()
      startRename()
      return
    }
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    toggleFolder()
  }
  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // The row's own keys (Enter, Space, F2) don't reach it while typing.
    event.stopPropagation()
    if (event.key === 'Enter') {
      event.preventDefault()
      finishRename(true, true)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      finishRename(false, true)
    }
  }

  const folderOptionsSections: MenuSection[] = [
    {
      items: [
        {
          type: 'action',
          id: 'rename-folder',
          label: 'Rename folder',
          icon: <EditIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          omitted: !renameFolder,
          onClick: startRename,
        },
      ],
    },
    {
      items: [
        {
          type: 'action',
          id: 'delete-folder',
          label: 'Delete folder',
          icon: <TrashIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          tone: 'danger',
          omitted: !deleteFolder,
          onClick: () => deleteFolder?.(folder.id),
        },
      ],
    },
  ]

  return (
    <div
      ref={setSortableRef}
      style={{
        // While dragging, the in-list row is hidden; the DragOverlay renders the
        // moving copy and neighbors slide via the sort strategy.
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      }}
    >
      <div
        ref={(node) => {
          setDropRef(node)
          rowRef.current = node
        }}
        style={{
          marginLeft: nestedRowInset,
          paddingLeft: folderRowPaddingLeft,
          width: nestedRowInset ? `calc(100% - ${nestedRowInset}px)` : '100%',
          minWidth: 0,
        }}
        // While a table is dragged over the folder, the pointer is over it
        // too: the drop highlight replaces the hover background, not under it.
        className={`group relative flex h-[var(--sidebar-tile-size)] w-full min-w-0 items-center rounded-md ${MENU_ROW_END_PAD_CLASS} text-left text-sm text-[color:var(--sidebar-submenu-text-data)] ${
          isDropTarget
            ? 'bg-[color:var(--surface-info-subtle)] ring-1 ring-[color:var(--border-info)]'
            : isRenaming
              ? 'bg-[color:var(--sidebar-item-hover-bg)]'
              : 'hover:bg-[color:var(--sidebar-item-hover-bg)]'
        }`}
        // While renamed, the row holds the name field and is no button.
        role={isRenaming ? undefined : 'button'}
        tabIndex={isRenaming ? undefined : 0}
        aria-expanded={isRenaming ? undefined : isExpanded}
        onClick={isRenaming ? undefined : toggleFolder}
        onKeyDown={isRenaming ? undefined : handleRowKeyDown}
      >
        {rules.reorderEnabled && (
          <LeadingDragHandle
            left={folderRowPaddingLeft + FOLDER_CHEVRON_SIZE + FOLDER_CHEVRON_ICON_GAP}
            size={FOLDER_ICON_SIZE}
            listeners={listeners}
            attributes={attributes}
            disabled={!canMoveFolder}
            disabledReason="No other folder to move it beside or into"
          />
        )}
        <div className="flex min-w-0 flex-1 items-center text-left">
          <ChevronDownIcon
            size={10}
            style={{ marginRight: FOLDER_CHEVRON_ICON_GAP }}
            className={`shrink-0 text-[color:var(--sidebar-submenu-text-faint)] transition-transform ${
              isExpanded ? '' : '-rotate-90'
            }`}
          />
          {isExpanded ? (
            <FolderOpenIcon
              size={FOLDER_ICON_SIZE}
              style={{ marginRight: FOLDER_ICON_LABEL_GAP }}
              className={`shrink-0 text-[color:var(--sidebar-submenu-text-faint)] ${swappedIconClass}`}
            />
          ) : (
            <FolderIcon
              size={FOLDER_ICON_SIZE}
              style={{ marginRight: FOLDER_ICON_LABEL_GAP }}
              className={`shrink-0 text-[color:var(--sidebar-submenu-text-faint)] ${swappedIconClass}`}
            />
          )}
          {isRenaming ? (
            <input
              ref={nameInputRef}
              type="text"
              aria-label="Folder name"
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              onKeyDown={handleNameKeyDown}
              onBlur={() => finishRename(true, false)}
              onPointerDown={(event) => event.stopPropagation()}
              className="h-6 min-w-0 flex-1 rounded border border-[color:var(--border-info)] surface-default px-1.5 text-sm text-[color:var(--sidebar-submenu-text-data)] outline-none"
            />
          ) : (
            <FadeTruncatedText>{folder.name}</FadeTruncatedText>
          )}
        </div>
        {/* Only the controls keep their clicks; the space around them is the
            row's, which is hovered and so toggles the folder like the rest.
            The last slot is empty: a folder has no heart, and its options
            line up with a table's. */}
        <div
          className={`ml-auto h-6 ${
            isRenaming ? 'hidden' : menuIconSlotsLeadClass(menuOpen)
          } ${MENU_ICON_SLOTS_CLASS}`}
        >
          {/* The menu's clicks and keys (its trigger's, and its items' through
              the portal) stay its own and never toggle the folder. */}
          <span
            className={menuIconSlotClass(menuOpen ? 'shown' : 'collapsed')}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <DropdownMenu
              open={menuOpen}
              onOpenChange={setMenuOpen}
              sections={folderOptionsSections}
              align="right"
              placement="right-start"
              anchorElementRef={rowRef}
              className="min-w-52"
              trigger={
                <IconButton
                  label="Folder options"
                  className={`${MENU_ICON_SLOT_CLASS} text-[color:var(--sidebar-submenu-text-faintest)] transition-colors hover:text-[color:var(--sidebar-submenu-text-data)]`}
                >
                  <KebabIcon size={14} aria-hidden="true" />
                </IconButton>
              }
            />
          </span>
          <span
            className={`h-6 w-6 shrink-0 ${menuIconSlotClass(menuOpen ? 'held' : 'collapsed')}`}
            aria-hidden="true"
          />
        </div>
      </div>

      {isExpanded && (
        <div className="relative space-y-[var(--sidebar-item-inset)]">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-1 top-0 w-px -translate-x-1/2 bg-[color:var(--sidebar-submenu-border)]"
            style={{ left: childGuideLeft }}
          />
          <SortableContext items={childFolderIds} strategy={rowStrategy}>
            {folder.children.map((child) => (
              <MemoizedFolderItem
                key={child.id}
                folder={child}
                tablesById={tablesById}
                depth={depth + 1}
                siblingFolderCount={folder.children.length}
              />
            ))}
          </SortableContext>
          <SortableContext items={tableIds} strategy={rowStrategy}>
            {folder.tables.map((tableRef) => {
              const table = tablesById.get(tableRef.table_id)
              if (!table) return null
              return (
                <TableLink
                  key={table.id}
                  table={table}
                  labelInset={getFolderLabelLeft(depth)}
                  highlightInset={getChildRowInset(depth)}
                  sortableId={rules.tableDragEnabled ? `table-${table.id}` : undefined}
                />
              )
            })}
          </SortableContext>
        </div>
      )}
    </div>
  )
}

const MemoizedFolderItem = memo(FolderItem)
export default MemoizedFolderItem
